"use client";

import { useEffect } from "react";

/**
 * Dev-only: applies the styleguide playground's "preview on all pages" tokens
 * on every route, so Chris can tune sliders on /styleguide and walk the real
 * app wearing the draft theme. Reads localStorage (device-local view
 * preference — the themes themselves are saved server-side) and reacts to
 * same-tab updates via the custom event and cross-tab updates via `storage`.
 * Renders nothing; no-ops entirely in production builds.
 */
const KEY = "tm-theme-preview";
export const THEME_PREVIEW_KEY = KEY;
export const THEME_PREVIEW_EVENT = "tm-theme-preview";

export function ThemePreviewApplier() {
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const el = document.documentElement;
    let applied: string[] = [];

    const apply = () => {
      for (const k of applied) el.style.removeProperty(k);
      applied = [];
      let raw: string | null = null;
      try {
        raw = localStorage.getItem(KEY);
      } catch {
        return;
      }
      if (!raw) return;
      try {
        const tokens = JSON.parse(raw) as Record<string, string>;
        for (const [k, v] of Object.entries(tokens)) {
          if (k.startsWith("--tm-") && k !== "--tm-ctl-state" && typeof v === "string") {
            el.style.setProperty(k, v);
            applied.push(k);
          }
        }
      } catch {
        // bad JSON — drop the preview rather than half-applying
        try {
          localStorage.removeItem(KEY);
        } catch {}
      }
    };

    apply();
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) apply();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(THEME_PREVIEW_EVENT, apply);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(THEME_PREVIEW_EVENT, apply);
      for (const k of applied) el.style.removeProperty(k);
    };
  }, []);
  return null;
}
